<script lang="ts" setup>
import { onMounted, onUnmounted } from 'vue'
import { useElementClick } from '@/composables/tracks/useElementClick'
import { resources } from '@/config/brands'
import { useRewardsRedDotStore } from '@/stores'
import { t } from '@/i18n/utils'

withDefaults(
  defineProps<{
    tab?: 'history' | 'home' | 'rewards' | 'member'
  }>(),
  {
    tab: 'home',
  },
)
const { elementClick } = useElementClick()

// 获取 Tab 图标配置（直接使用配置中的完整路径）
const tabIcons = resources.tabIcons

// Rewards Tab 红点：存在未领取的签到/Diamond Card 每日奖励时展示
const redDotStore = useRewardsRedDotStore()

// 回到前台时强制刷新红点状态
function onVisibilityChange() {
  if (document.visibilityState === 'visible')
    redDotStore.refresh({ force: true })
}

// 预加载所有图标，避免切换时闪烁
onMounted(() => {
  const iconsToPreload = [
    tabIcons.library,
    tabIcons.libraryActive,
    tabIcons.novel,
    tabIcons.novelActive,
    tabIcons.rewards,
    tabIcons.rewardsActive,
    tabIcons.profile,
    tabIcons.profileActive,
  ]

  iconsToPreload.forEach((src) => {
    const img = new Image()
    img.src = src
  })

  redDotStore.refresh()
  document.addEventListener('visibilitychange', onVisibilityChange)
})

onUnmounted(() => {
  document.removeEventListener('visibilitychange', onVisibilityChange)
})
</script>

<template>
  <BasicFixedBottom>
    <div class="flex w-full text-n-text-2 bg-n-bg-pure tabbar-line">
      <div class="flex-1 text-center py-6px">
        <router-link
          :to="{ name: 'history' }" replace
          :class="{ 'tab-active font-bold': tab === 'history' }"
        >
          <div @click="elementClick('bookshelf')">
            <img
              :src="tab === 'history' ? tabIcons.libraryActive : tabIcons.library"
              class="mx-a h-6 w-6"
              alt="Library"
            >
            <p class="mt-1 text-11px tab-text">
              {{ t('LibraryTab') }}
            </p>
          </div>
        </router-link>
      </div>
      <div class="flex-1 text-center py-6px">
        <router-link
          :to="{ name: 'home' }" replace
          :class="{ 'tab-active font-bold': tab === 'home' }"
        >
          <div @click="elementClick('novel')">
            <img
              :src="tab === 'home' ? tabIcons.novelActive : tabIcons.novel"
              class="mx-a h-6 w-6"
              alt="Novel"
            >
            <p class="mt-1 text-11px tab-text">
              {{ t('NovelTab') }}
            </p>
          </div>
        </router-link>
      </div>
      <div class="flex-1 text-center py-6px">
        <router-link
          :to="{ name: 'welfare' }" replace
          :class="{ 'tab-active font-bold': tab === 'rewards' }"
        >
          <div @click="elementClick('rewards')">
            <div class="relative inline-block">
              <img
                :src="tab === 'rewards' ? tabIcons.rewardsActive : tabIcons.rewards"
                class="mx-a h-6 w-6"
                alt="Rewards"
              >
              <span v-if="redDotStore.showRedDot" class="reward-red-dot" />
            </div>
            <p class="mt-1 text-11px tab-text">
              {{ t('RewardsTab') }}
            </p>
          </div>
        </router-link>
      </div>
      <div class="flex-1 text-center py-6px">
        <router-link
          :to="{ name: 'member' }" replace
          :class="{ 'tab-active font-bold': tab === 'member' }"
        >
          <div @click="elementClick('profile')">
            <img
              :src="tab === 'member' ? tabIcons.profileActive : tabIcons.profile"
              class="mx-a h-6 w-6"
              alt="Profile"
            >
            <p class="mt-1 text-11px tab-text">
              {{ t('ProfileTab') }}
            </p>
          </div>
        </router-link>
      </div>
    </div>
  </BasicFixedBottom>
</template>

<style scoped>
/* 顶部 1px 分割线 */
.tabbar-line {
  border-top: 0.5px solid rgba(255, 255, 255, 0.08);
}

/* Rewards 红点：右上角小圆点 */
.reward-red-dot {
  position: absolute;
  top: -2px;
  right: -4px;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #ff4d4f;
}

/* 选中态文字：渐变色 */
.tab-active .tab-text {
  background-image: linear-gradient(270deg, rgba(255, 107, 210, 1) 0%, rgba(161, 98, 255, 1) 100%);
  background-clip: text;
  -webkit-background-clip: text;
  color: transparent;
}
</style>
